import React from 'react';
import { flushSync } from 'react-dom'; // eslint-disable-line no-restricted-imports
import ReactRouterPropTypes from 'react-router-prop-types';
import PropTypes from 'prop-types'; // eslint-disable-line no-restricted-imports

import { ConfigContext } from 'Context/config';

import { stripStringAndCamelCase, titleCaseFirstLetter } from 'Utilities';
import metatitle from 'Utilities/metatitle';
import T from 'Utilities/texts';
import { SELL_MY_CAR_LANDING_PAGE_V2, SELL_MY_CAR_PATH } from 'Utilities/wordpress';
import { fetchLandingPage } from 'Utilities/wordpressPages';

import { ComponentContent, CONTENT_SIZES } from 'Layout';

import StickyVRM from '../../../../components/misc/StickyVRM/StickyVRM';
import TrustpilotReviewsCarousel from '../../../../components/reviews/ReviewsCarousel/TrustpilotReviewsCarousel';
import DialogLoader from '../../../../components/transitions/DialogLoader/DialogLoader';
import { connect } from '../../../../store';
import { RoundedSection } from '../../common/RoundedSection/RoundedSection';
import Status404 from '../../error/404';
import DealerLogos from '../../fastlane/dealerLogos/DealerLogos';
import Hero from '../../fastlane/home/Hero/Hero';
import USP from '../../fastlane/home/Usp/Usp';
import RecentSales from '../../fastlane/recentSales/RecentSales';
import VideoSection from '../../fastlane/videoSection/VideoSection';
import HowItWorks from '../../landingPages/Components/HowItWorks/HowItWorks';
import { RelatedGuidesSection } from '../../landingPages/Components/RelatedGuidesSection/RelatedGuidesSection';
import { landingPageSectionLoadEvent, PAGE_SECTIONS } from '../../landingPages/Pages/LandingPage.helpers';

import BodyArea from './BodyArea/BodyArea';
import FAQSection, { createFAQData } from './FAQSection/FAQSection';

import styles from './LandingPages.scss';

const numWord = ['one', 'two', 'three', 'four', 'five', 'six'];

const sectionNames = numWord.slice(0, 4).map((n, i) => {
  const isEven = (i % 2 === 0);

  return ({
    contentBody: `section_${n}_body_area`,
    imageAlt: `section_${n}_image_area_alt`,
    imageArea: `section_${n}_image_area`,
    position: (isEven) ? 'left' : 'right',
    theme: (isEven) ? 'light' : 'dark',
    title: `section_${n}_title`,
  });
});

const relatedGuidesNames = numWord.map((n) => ({
  description: `related_guide_description_${n}`,
  thumbnail: `related_guide_image_${n}`,
  title: `related_guide_title_${n}`,
  url: `related_guide_url_${n}`,
}));

class LandingPage extends React.PureComponent {
  constructor(props, fastlaneContext) {
    super(props);
    this.$content = React.createRef();

    const { context } = this.props;

    this.fastlaneContext = fastlaneContext;
    const { configState } = this.fastlaneContext;

    // On the server, landingPageData will always be in identical, only the client can change the fastlane context,
    // if it does set it to null, then we know that the user has come from a fastlaneContext page so fetch data from API
    const landingPageData = (configState.landingPageData === null)
      ? {}
      : context.state.landingPageData || {};

    const acf = landingPageData.content;

    this.state = { acf, failedReviewsCarousel: false };

    if (!acf) {
      this.state.loading = true;
    }
  }

  async componentDidMount() {
    await this.fetchLandingPageData();
    this.updateMetaTags();

    this.setState({
      stickyVRM: true,
    });
  }

  async componentDidUpdate(prevProps) {
    const { match } = this.props;

    if (prevProps.match.url !== match.url && !match.url.endsWith('/mileage')) {
      await this.fetchLandingPageData(true);
      this.updateMetaTags();
    }
  }

  componentWillUnmount() {
    // TODO - As part of Wordpress refactor, clean up wordpressData/landingPage store
    const { context } = this.props;
    const { configActions } = this.fastlaneContext;

    const updatedData = {
      landingPageData: null,
    };

    configActions.update(updatedData);
    context.update(updatedData);
  }

  updateMetaTags = () => {
    const { acf } = this.state;
    const { match } = this.props;
    const { vehicle } = match.params;
    const section = stripStringAndCamelCase(match.path);

    const title = titleCaseFirstLetter((acf && (acf.seo_title || acf.header_title))
      || T.translate(`landing${section}View.title`, { vehicle }));

    const description = titleCaseFirstLetter((acf && (acf.seo_description || acf.header_description))
      || T.translate(`landing${section}View.description`, { vehicle }));

    metatitle.formatTitle(window.location.pathname, title);
    metatitle.formatDescription(description);
  };

  fetchLandingPageData = async (force) => {
    const { match: { url } } = this.props;
    const { acf } = this.state;

    if (force || !acf) {
      this.setState({ loading: true });

      const landingPageUrlType = url.split('/sell-my-car/').join('');
      const landingPageUrl = landingPageUrlType === 'mileage' ? SELL_MY_CAR_PATH : landingPageUrlType;

      const landingPageData = await fetchLandingPage(
        landingPageUrl.replace(/\/mileage\/?$/, ''),
      );

      const newAcf = landingPageData.content;

      flushSync(() => {
        this.setState({
          acf: newAcf,
          loading: false,
        }, () => {
          this.setState({
            stickyVRM: true,
          });
        });
      });
    }
  };

  filterComponents = (components) => {
    const { acf } = this.state;

    return components.filter((component) => acf[component.title]);
  };

  displayHeroArea = () => {
    const { acf } = this.state;

    if (!acf.header_title) {
      return null;
    }

    return (
      <Hero
        onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.HERO)}
        subTitle={acf.header_description?.trim()}
        title={acf.header_title}
      />
    );
  };

  displayBodyArea = () => {
    const { acf } = this.state;

    return this.filterComponents(sectionNames).map(
      ({
        contentBody, imageAlt, imageArea, position, theme, title,
      }) => (
        <BodyArea
          key={title}
          contentBody={acf[contentBody]}
          imgAlt={acf[imageAlt]}
          imgSrc={acf[imageArea]}
          position={position}
          theme={theme}
          title={acf[title]}
        />
      ),
    );
  };

  displayRelatedGuides = () => {
    const { acf } = this.state;

    return this.filterComponents(relatedGuidesNames).map(({
      description, thumbnail, title, url,
    }) => ({
      description: acf[description],
      thumbnail: acf[thumbnail],
      title: acf[title],
      url: acf[url] || '',
    }));
  };

  displayRelatedGuidesContent = (faq) => {
    const { acf } = this.state;

    const { match: { url } } = this.props;

    const relatedGuides = this.displayRelatedGuides();

    if (!relatedGuides || relatedGuides.length === 0) {
      return null;
    }

    return (
      <RelatedGuidesSection
        guides={relatedGuides}
        headerTitle={acf.related_guide_header}
        isFAQEmpty={!faq}
        onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.RELATED_GUIDES)}
        redirectToUrl={url} />
    );
  };

  displayFAQSection = (data) => {
    const { acf: { faq_title: faqTitle } } = this.state;

    if (!Array.isArray(data) || !data.length) {
      return null;
    }

    return (
      <FAQSection faq={data} onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.FAQ)} title={faqTitle} />
    );
  };

  setFailedReviewsCarousel = () => {
    this.setState((prevState) => ({ failedReviewsCarousel: !prevState.failedReviewsCarousel }));
  };

  render() {
    const {
      acf, failedReviewsCarousel, loading, stickyVRM,
    } = this.state;
    const $content = this.$content.current;
    const faq = createFAQData(acf);
    const faqNotEmpty = Array.isArray(faq) && faq.length;
    const { match: { url } } = this.props;

    if (loading) {
      return (<DialogLoader />);
    }

    if (!acf) {
      return (<Status404 />);
    }

    // Testing a leaner version of the '/sell-my-car' landing page
    if (url === SELL_MY_CAR_LANDING_PAGE_V2) {
      return (
        <div ref={this.$content} className={styles.viewV2}>
          <div className={styles.heroSection}>
            {this.displayHeroArea()}
            <USP onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.USP)}/>
            <HowItWorks className={styles.stepsSectionWrapper} stepsClassName={styles.steps} />
            <ComponentContent
              className={failedReviewsCarousel ? styles.trustpilotEmpty : styles.trustpilot}
              maxWidth={CONTENT_SIZES.none}
            >
              <TrustpilotReviewsCarousel
                largeTitle
                failedLoad={failedReviewsCarousel}
                onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.TRUSTPILOT)}
                setFailedLoad={this.setFailedReviewsCarousel}
              />
            </ComponentContent>
          </div>
        </div>
      );
    }

    return (
      <div ref={this.$content} className={styles.view}>
        <div className={styles.heroSection}>
          {this.displayHeroArea()}

          <RoundedSection withBottomLedge>
            <USP className={styles.usp} onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.USP)}/>
          </RoundedSection>
        </div>

        <RoundedSection withBottomLedge withTopLedge className={styles.videoWrapper} theme="off-white">
          <VideoSection onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.VIDEO)} />
        </RoundedSection>

        {
          stickyVRM && $content && (
            <StickyVRM
              {...{
                $content,
                mobileOnly: true,
              }}
            />
          )
        }

        <RecentSales onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.RECENT_SALES)}/>

        <RoundedSection withBottomLedge withTopLedge theme="off-white">
          <HowItWorks />
        </RoundedSection>

        <RoundedSection withBottomLedge withTopLedge>
          <ComponentContent
            className={failedReviewsCarousel ? styles.trustpilotEmpty : styles.trustpilot}
            maxWidth={CONTENT_SIZES.none}
          >
            <TrustpilotReviewsCarousel
              largeTitle
              failedLoad={ failedReviewsCarousel }
              onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.TRUSTPILOT)}
              setFailedLoad={this.setFailedReviewsCarousel}
            />
          </ComponentContent>
        </RoundedSection>

        <RoundedSection withBottomLedge withTopLedge theme="off-white">
          <DealerLogos onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.DEALER_LOGOS)}/>
        </RoundedSection>

        <RoundedSection withBottomLedge withTopLedge className={styles.wrapper}>
          {this.displayBodyArea()}
          {this.displayFAQSection(faq)}
          {this.displayRelatedGuidesContent(faqNotEmpty)}
        </RoundedSection>
      </div>
    );
  }
}

LandingPage.contextType = ConfigContext;

LandingPage.propTypes = {
  context: PropTypes.object.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
};

export default connect(LandingPage);
